import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

@Component({
    tag: "ux-dropdown",
    styleUrl: "dropdown.scss",
    shadow: false
})
export class Dropdown {
    private baseClass = "ux-dropdown";
    @State() isOpen = false;
    @State() activeLabel: null | string = null;
    @State() selectedCheckboxes = 0;

    /**
     * Set parent container to calculate max width of dropdown and activating scrollbar overflow if necessary
     */
    @Prop() readonly parentContainerSelector: string = "";

    /**
     * Text displayed inside the Dropdown
     */
    @Prop() readonly placeholder: string = "";

    /**
     * Name Identifies the dropdown in a bigger context (for example in forms, filters)
     */
    @Prop({ reflect: true }) readonly name: string = "";

    /**
     * This toggles the text-input to filter the dropdown items
     */
    @Prop({ reflect: true }) readonly filterable: boolean = false;

    /**
     * The placeholder string for the search field
     */
    @Prop() readonly searchPlaceholder: string = "Search";

    @State() hasRadioListItems = false;
    @State() hasCheckboxListItems = false;
    @State() dropdownListStyles: { overflowY: string; maxHeight: string } = {
        overflowY: "auto",
        maxHeight: "none"
    };

    @Element() domNode!: HTMLUxDropdownElement;

    private dropdownList: HTMLUxFilterableListElement;
    private parentContainer: HTMLElement | null = null;
    private mimimumAcceptableHeight = 200;
    private windowBottomPadding = 20;

    @Listen("click", { target: "body" })
    handleBodyClick(event: UIEvent): void {
        if (!this.domNode.contains(event.target as Node)) {
            this.isOpen = false;
        }
    }

    /** Connect filter */
    @Watch("parentContainerSelector")
    setParentContainer() {
        if (this.parentContainerSelector === "") {
            return;
        }
        this.parentContainer = this.domNode.closest(this.parentContainerSelector) as HTMLElement;
    }

    componentDidLoad() {
        window.requestAnimationFrame(() => {
            this.hasRadioListItems = !!this.dropdownList.querySelector("ux-radio-list-item");
            this.hasCheckboxListItems = !!this.dropdownList.querySelector("ux-checkbox-list-item");
            this.setSelectedCheckboxes();
            this.getActiveLabel();
            this.setParentContainer();
        });
    }

    private getActiveLabel = async () => {
        if (!this.hasRadioListItems) {
            return;
        }
        let checkedRadioButton = this.domNode.querySelector("input[type='radio']:checked")?.closest("ux-radio-button");
        if (!checkedRadioButton && this.placeholder === "") {
            const firstRadioListItem = this.domNode.querySelector("input[type='radio']")?.closest("ux-radio-list-item");
            // @ts-expect-error Check for typing
            firstRadioListItem.checked = true;
            // @ts-expect-error Check for typing
            checkedRadioButton = firstRadioListItem.querySelector("ux-radio-button");
        } else {
            this.activeLabel = null;
        }
        // @ts-expect-error Check for typing
        this.activeLabel = await checkedRadioButton?.getLabel();
    };

    @Listen("uxRadioButtonChange", { target: "window" })
    async handleOneChoice(e: CustomEvent) {
        if (!this.hasRadioListItems || !this.dropdownList) {
            return;
        }
        if (!this.dropdownList.contains(e.target as HTMLElement)) {
            return;
        }
        this.activeLabel = await (e.target as HTMLUxRadioButtonElement).getLabel();
        this.isOpen = false;
    }

    @Listen("uxCheckboxListItemChange", { target: "window" })
    async handleMultipleChoice(e: CustomEvent) {
        if (!this.hasCheckboxListItems || !this.dropdownList) {
            return;
        }
        if (!this.dropdownList.contains(e.target as HTMLElement)) {
            return;
        }
        this.setSelectedCheckboxes();
    }

    private setSelectedCheckboxes = () => {
        if (!this.hasCheckboxListItems || !this.dropdownList) {
            return;
        }
        this.selectedCheckboxes = this.dropdownList.querySelectorAll("input[type='checkbox']:checked").length;
    };

    private onLabelClick(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.enableScrollBarOnOverflow();
        } else {
            setTimeout(() => {
                if (this.isOpen) {
                    return;
                }
                this.dropdownListStyles = {
                    overflowY: "auto",
                    maxHeight: "none"
                };
            }, 300);
        }
    }

    private enableScrollBarOnOverflow(): void {
        if (this.dropdownList) {
            const scrollHeight = this.dropdownList.scrollHeight;
            const windowHeight = window.innerHeight;
            let availableHeight = 0;
            let isParentContainerDefined = false;
            const listRects = this.dropdownList.getBoundingClientRect();
            const availableDropHeight = windowHeight - listRects.top;
            if (this.parentContainer !== null) {
                const parentRects = this.parentContainer.getBoundingClientRect();
                const listOffsetYToParent = listRects.top - parentRects.top;
                const parentStyles = window.getComputedStyle(this.parentContainer);
                const paddingBottomParent = parseFloat(parentStyles.getPropertyValue("padding-bottom"));
                availableHeight = parentRects.height - listOffsetYToParent - paddingBottomParent;
                isParentContainerDefined = true;
            } else {
                availableHeight = availableDropHeight;
            }
            const optimalHeight = !isParentContainerDefined
                ? availableHeight - this.windowBottomPadding
                : availableHeight < availableDropHeight
                  ? availableHeight
                  : availableDropHeight - this.windowBottomPadding;
            const maxHeight = optimalHeight > this.mimimumAcceptableHeight ? optimalHeight : this.mimimumAcceptableHeight;
            if (scrollHeight > maxHeight) {
                this.dropdownListStyles = {
                    overflowY: "scroll",
                    maxHeight: maxHeight + "px"
                };
            }
        }
    }

    public render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--down`]: this.isOpen
                }}
            >
                <button
                    class={{
                        [`${this.baseClass}__label`]: true,
                        [`${this.baseClass}__label--light`]: this.hasRadioListItems
                    }}
                    onClick={this.onLabelClick.bind(this)}
                    type="button"
                >
                    <span>
                        <span innerHTML={this.activeLabel || this.placeholder} />
                        {this.hasCheckboxListItems && this.selectedCheckboxes > 0 && (
                            <span class="ux-dropdown__counter">{this.selectedCheckboxes}</span>
                        )}
                    </span>
                    <ux-icon name="chevron-down" weight="bold" />
                </button>
                <div class="ux-dropdown__dropdown-locator" aria-hidden={`${!this.isOpen}`}>
                    <ux-filterable-list
                        inert={!this.isOpen}
                        filterable={this.filterable}
                        search-placeholder={this.searchPlaceholder}
                        class={{
                            [`${this.baseClass}__menu`]: true,
                            [`${this.baseClass}__menu--radio-items`]: this.hasRadioListItems
                        }}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.dropdownList = el)}
                        style={{
                            overflowY: this.dropdownListStyles.overflowY,
                            maxHeight: this.dropdownListStyles.maxHeight
                        }}
                    >
                        <slot />
                    </ux-filterable-list>
                </div>
            </Host>
        );
    }
}
